import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-listing-card>` says about itself.
 *
 * ── TWO INPUTS ARE DECLARED AND DRAWN NOWHERE, ON PURPOSE ────────────────────
 * `rating_avg` and `install_count` are real columns on the Exchange's Listing object and this card
 * renders neither. They are declared here all the same, and the reason is the input contract rather
 * than optimism: an input name is the key a stored placement writes its value under, so a
 * component's inputs freeze the moment it becomes placeable, and the Component metatype refuses a
 * placeable component whose inputs are undeclared. Adding an input after placements exist is the
 * expensive migration; declaring two now costs two entries and lets the ticket that fills those
 * columns switch them on without touching the manifest or any stored placement.
 *
 * Why they are not drawn: both fields say of themselves that nothing writes them. `install_count`
 * carries "NO WRITER EXISTS YET ... a stored column that nothing currently fills", and `rating_avg`
 * names the review-aggregation ticket as its future writer. So they are not sparse in a fixture,
 * they are empty in production for every listing until that lands — and a rating slot blank on every
 * card reads as "this listing has no ratings", not as "the platform has none". Each description
 * below says so, because an input that is accepted and ignored is a trap unless its own line admits
 * it.
 *
 * ── THERE IS NO `verified` INPUT, AND THAT IS NOT AN OMISSION ────────────────
 * A marketplace card wants a publisher verification badge and this one has no input for it, because
 * the Exchange's Publisher object has no verification field: it carries `bio`, `company_name`,
 * `logo_url`, `publisher_org_id`, `slug` and `support_url`. Accepting a `verified` boolean would let
 * a page author assert a verification the platform never performs, which is worse than the gap.
 *
 * ── `reviewed` IS A BOOLEAN BECAUSE THE FIELD BEHIND IT IS ───────────────────
 * It maps to `listing.review_badge`, the public projection of an operator-only Select that is
 * `classification: sensitive` and "never" readable by a visitor. The Boolean cannot distinguish
 * pending from flagged, which is the point: a richer input here would invite a page to source it
 * from the sensitive field and leak a verdict-in-progress to the public web. One bit, one claim.
 */
const docs: ComponentDocs = {
  aliases: [
    'marketplace card',
    'package card',
    'app card',
    'product card',
    'catalog card',
    'listing tile',
    'exchange card',
    'store card',
  ],

  answers: [
    'show one package in a marketplace grid',
    'let somebody browse packages and choose one',
    'link a visitor from a browse page to a listing',
    'list the packages one publisher has published',
    'put a curated collection of packages on a page',
  ],

  // `recordPage` is here for the publisher profile: a Publisher record page lists the listings that
  // publisher has published, and those are these cards. `homePage` and `appPage` are the marketplace
  // home and its category and collection pages. Not `recordAction`, `utilityBar`, `screen` or `seam`
  // — a card is a thing you choose between, not an action, a tool, a flow step or frame furniture.
  placements: ['homePage', 'appPage', 'recordPage'],

  inputs: [
    {
      name: 'listing_title',
      attribute: 'listing-title',
      type: 'text',
      description:
        'The listing name, and the card\'s accessible name. Called listing-title rather than title ' +
        'because title is a global HTML attribute: observing it would give every card a browser ' +
        'tooltip of its own heading and have a screen reader read the name twice.',
    },
    {
      name: 'tagline',
      attribute: 'tagline',
      type: 'text',
      description:
        'One line under the title saying what the package is for. Clamped to two rendered lines so ' +
        'a long one cannot change a card\'s height in a grid of them — the string is not truncated, ' +
        'only what is shown.',
    },
    {
      name: 'publisher_name',
      attribute: 'publisher-name',
      type: 'text',
      description:
        'Who publishes it, as a mono eyebrow above the title. Drawn as text, which is why the logo ' +
        'below is marked decorative rather than named again for a screen reader.',
    },
    {
      name: 'publisher_logo',
      attribute: 'publisher-logo',
      type: 'text',
      description:
        "The publisher's logo URL — publisher.logo_url. This is the PUBLISHER's mark, not artwork " +
        'for the listing: Listing has no media, screenshot, gallery or hero column, so there is no ' +
        'per-listing image to show. Absent falls back to the package glyph, never a broken image.',
    },
    {
      name: 'package_type',
      attribute: 'package-type',
      type: 'text',
      // Open rather than closed. The six are the shipped taxonomy, but the value set is a published
      // vocabulary that can gain a value, and a closed `options` here would become a constraint the
      // platform enforces on placements — refusing a seventh type the Exchange had legitimately
      // added. `fallback` says the true thing: these are the current members, anything else draws
      // as given.
      fallback: 'drawn as given, in a neutral tag',
      options: ['App', 'Object Pack', 'Field Pack', 'Calc Blueprint', 'Industry Template', 'AI Agent'],
      description:
        'The package-type label, already resolved to its display form — the card does not map a ' +
        'picklist value to a label. Drawn as a neutral <caos-tag>.',
    },
    {
      name: 'pricing_label',
      attribute: 'pricing-label',
      type: 'text',
      fallback: 'drawn as given, in a neutral tag',
      options: ['Free', 'Paid', 'Usage-based'],
      description:
        'The pricing label, already resolved — the card does not price anything or format a figure. ' +
        'Drawn as a second neutral <caos-tag> beside the type.',
    },
    {
      name: 'reviewed',
      attribute: 'reviewed',
      type: 'boolean',
      default: false,
      description:
        'Whether a security review passed, from listing.review_badge. Shows a success pill reading ' +
        '"Reviewed". Deliberately a boolean: the field behind it is the public projection of an ' +
        'operator-only status that a visitor must never read, and it cannot distinguish pending ' +
        'from flagged. Never source this from security_review_status. Unlike an ordinary HTML ' +
        'boolean attribute, a present value that spells out falsehood counts as FALSE rather than as ' +
        'present-and-therefore-true: a stored placement serialises through metadata as a string, and ' +
        'the failure that matters here badges an unreviewed package as reviewed. The off set is ' +
        'false, 0, no and off, compared after trimming and lowercasing, so "False", "FALSE" and ' +
        '" false " are all off; any other present value, including an empty one, is on.',
    },
    {
      name: 'href',
      attribute: 'href',
      type: 'text',
      description:
        "The listing's detail route, e.g. /listing/cloud-atlantis/sales-atlas. The whole card " +
        'is this one link, so it takes focus and is announced as a link without anything else being ' +
        'arranged. An unmodified click raises caos-listing-select and THEN NAVIGATES NORMALLY unless ' +
        'a listener cancels that event, so a card placed on a page with no router still works as a ' +
        'link; a host that routes in-app calls preventDefault() on it to suppress the page load. A ' +
        'modified click is left entirely alone, so open-in-new-tab and copy-address still work. With ' +
        'no href the card draws, raises nothing, and does not navigate. The route must resolve to this ' +
        "workspace's OWN ORIGIN: a relative path, or an absolute http/https URL on the same origin. " +
        'Anything else is refused and the card behaves as though it had no href, on the anchor and in ' +
        'the event alike — a javascript:, data:, vbscript:, file: or mailto: value, and also any route ' +
        'that merely leaves the site, which includes another origin and the protocol-relative //host ' +
        'and backslash forms that resolve to one. Both halves are part of this input because the card ' +
        'follows the link itself when nothing routes it: the scheme rule stops a URL that executes, ' +
        'and the origin rule stops an open redirect on the surface a visitor judges a package from.',
    },
    {
      name: 'rating_avg',
      type: 'number',
      description:
        'ACCEPTED AND DRAWN NOWHERE. The mean review rating, from listing.rating_avg. Declared so ' +
        'the input contract is settled before this card is placeable, because adding an input later ' +
        'is a breaking change against stored placements. Nothing writes that column yet — review ' +
        'aggregation is a separate ticket — so every listing would show an empty rating, which reads ' +
        'as "this one has none" rather than "there are none". It will be drawn when a writer exists.',
    },
    {
      name: 'install_count',
      type: 'number',
      description:
        'ACCEPTED AND DRAWN NOWHERE, for the same reason as rating_avg. From listing.install_count, ' +
        'whose own description says no writer exists: the Exchange package is metadata only and has ' +
        'no automation component, so the column is stored and unfilled. Declared now, rendered when ' +
        'something fills it.',
    },
  ],

  events: [
    {
      name: 'caos-listing-select',
      when:
        'the card is activated by an unmodified click, or by Enter on the focused link. Not raised ' +
        'for a modified click, which the browser handles as an ordinary link so open-in-new-tab and ' +
        'copy-address keep working.',
      carries:
        '{ href, title } — enough for a host router to navigate without a page load. Bubbles and ' +
        'crosses the shadow boundary. CANCELABLE, and cancelling it is the contract: call ' +
        'preventDefault() to take the navigation, and the card suppresses the browser page load. ' +
        'Leave it alone and the link follows href as normal, so a card with no router is still a ' +
        'working link rather than an inert one.',
    },
  ],

  state: [],

  slots: [],

  tokens: [
    {
      token: '--caos-color-heat',
      controls:
        'the border and focus ring on hover and focus — the ember. This is the second accent and not ' +
        '--caos-color-accent, which carries whatever primary hue the active brand sets and need not ' +
        'be warm at all. heat is a core token, defined by the default brand as well as any override, ' +
        'so reaching for it is not brand-coupling: a brand that moves heat moves this card with it. ' +
        'Neither token carries text here — accent can fall below the 4.5:1 AA floor — only a border ' +
        'and a ring.',
    },
    { token: '--caos-color-surface', controls: 'the card face, against the carbon ground the media well uses.' },
    { token: '--caos-color-bg', controls: "the carbon ground behind the publisher's mark, so the mark sits in a well rather than on the card face." },
    { token: '--caos-color-border', controls: 'the resting border, replaced by heat on hover and focus.' },
  ],

  parts: [
    { part: 'card', is: 'the anchor that is the whole card — the focusable, navigating element.' },
    { part: 'media', is: "the well holding the publisher's mark, or the fallback glyph." },
    { part: 'publisher', is: 'the mono eyebrow naming the publisher.' },
    { part: 'title', is: 'the listing name.' },
    { part: 'tagline', is: 'the two-line summary under the title.' },
    { part: 'chips', is: 'the bottom row: type tag, pricing tag, and the review pill.' },
  ],

  methods: [],

  placing:
    'Cards go in a grid on a marketplace page — the Exchange home, a category page, a collection ' +
    'row, or a publisher profile. The card fills its grid cell and stretches to the row height, so ' +
    'the chip row aligns across a row whatever each tagline\'s length. It brings no grid of its own: ' +
    'the page supplies the layout.',

  whenToUse:
    'When a visitor is choosing between packages and needs the publisher, the type, the price and ' +
    'whether it passed review before they click. For a record set that a person is working through ' +
    'rather than shopping, reach for CardGrid instead — its card is deliberately compact and it ' +
    'interchanges with DataTable, KanbanBoard and SplitView over the same records.',

  insteadUse: [
    {
      name: 'CardGrid',
      when:
        'the cards are a view of a record set rather than a catalogue. CardGrid draws a name, a ' +
        'sub-line and up to two metrics, and the same records can be switched to a table, a kanban ' +
        'or a split view without re-authoring. This card is the opposite trade: more facts, one ' +
        'shape, and a marketplace\'s vocabulary.',
    },
    {
      name: 'StatTile',
      when: 'the thing on the page is a figure rather than a thing to choose. A tile is a number with a label, not a link to a product.',
    },
  ],

  accessibility:
    'The card is a single anchor, so it is focusable, reachable by Tab, and announced as a link ' +
    'without a role or a keydown handler. Its accessible name is its CONTENT, and the card sets no ' +
    'aria-label: aria-label would replace the name rather than shorten it, so naming the card with ' +
    'the title alone would stop the package type, the price, the review mark and the tagline being ' +
    'read as part of the link — which is most of what the visitor is choosing between. The ' +
    'announcement is longer for that reason and the length is the point. The publisher logo is ' +
    'alt="" because the publisher name is already text in the card and would otherwise be read ' +
    'twice; the fallback glyph is aria-hidden for the same reason. The focus ring is on ' +
    ':focus-visible, so a pointer click does not leave one, and it is drawn as a transparent outline ' +
    'as well as a shadow so that forced-colors mode — which drops box-shadow — still shows one. ' +
    'An unattributed listing hides its publisher row rather than leaving an empty one. The host ' +
    'is responsible for one thing this piece cannot do: href must be a real route, because a card ' +
    'with no href still draws and is then a heading that looks clickable and is not.',

  // The examples are drawn from the platform's OWN first-party packages, and deliberately not from
  // any particular customer's application. `test/vocabulary.test.ts` enforces that boundary: the
  // platform is generic, a customer's business belongs to that customer's application and its own
  // documentation, and a component reference is something every customer of the platform reads. A
  // seeded marketplace in an org is a different matter — those are records, and a publisher's
  // listings are legitimately about that publisher's business. This file is platform source.
  examples: [
    {
      key: 'types',
      label: 'One card per package type',
      summary:
        'Six listings, one per browsable category, as they read across a grid. Three carry the ' +
        'review pill and three do not, so it is visible as a difference rather than as a constant.',
      placement: [
        {
          component: 'caos-listing-card',
          inputs: {
            'listing-title': 'Sales Atlas',
            tagline: 'Accounts, contacts, opportunities and orders in one application.',
            'publisher-name': 'Cloud Atlantis',
            'package-type': 'App',
            'pricing-label': 'Free',
            reviewed: true,
            href: '/listing/cloud-atlantis/sales-atlas',
          },
        },
        {
          component: 'caos-listing-card',
          inputs: {
            'listing-title': 'Service Objects',
            tagline: 'Cases, entitlements and service contracts, with their layouts and list views.',
            'publisher-name': 'Cloud Atlantis',
            'package-type': 'Object Pack',
            'pricing-label': 'Free',
            reviewed: true,
            href: '/listing/cloud-atlantis/service-objects',
          },
        },
        {
          component: 'caos-listing-card',
          inputs: {
            'listing-title': 'Tax Rules',
            tagline: 'A registered, versioned calculation other metadata can call.',
            'publisher-name': 'Cloud Atlantis',
            'package-type': 'Calc Blueprint',
            'pricing-label': 'Paid',
            reviewed: true,
            href: '/listing/cloud-atlantis/tax-rules',
          },
        },
        {
          component: 'caos-listing-card',
          inputs: {
            'listing-title': 'Field Service Starter',
            tagline: 'A starting configuration for field service, meant to be edited after install.',
            'publisher-name': 'Cloud Atlantis',
            'package-type': 'Industry Template',
            'pricing-label': 'Free',
            href: '/listing/cloud-atlantis/field-service-starter',
          },
        },
        {
          component: 'caos-listing-card',
          inputs: {
            'listing-title': 'Address Verification',
            tagline: 'Verification fields over the objects you already have.',
            'publisher-name': 'Cloud Atlantis',
            'package-type': 'Field Pack',
            'pricing-label': 'Free',
            href: '/listing/cloud-atlantis/address-verification',
          },
        },
        {
          component: 'caos-listing-card',
          inputs: {
            'listing-title': 'Atlantis AI',
            tagline: 'An assistant grounded in the records it is allowed to read.',
            'publisher-name': 'Cloud Atlantis',
            'package-type': 'AI Agent',
            'pricing-label': 'Usage-based',
            href: '/listing/cloud-atlantis/atlantis-ai',
          },
        },
      ],
    },
    {
      key: 'reviewed',
      label: 'Reviewed, and not yet',
      summary:
        'The same listing with and without the review pill. There is no third state to show: the ' +
        'field behind the pill is a boolean precisely so that pending and flagged cannot be told ' +
        'apart from outside.',
      placement: [
        {
          component: 'caos-listing-card',
          inputs: {
            'listing-title': 'Sales Atlas',
            tagline: 'Accounts, contacts, opportunities and orders in one application.',
            'publisher-name': 'Cloud Atlantis',
            'package-type': 'App',
            'pricing-label': 'Free',
            reviewed: true,
            href: '/listing/cloud-atlantis/sales-atlas',
          },
        },
        {
          component: 'caos-listing-card',
          inputs: {
            'listing-title': 'Sales Atlas',
            tagline: 'Accounts, contacts, opportunities and orders in one application.',
            'publisher-name': 'Cloud Atlantis',
            'package-type': 'App',
            'pricing-label': 'Free',
            href: '/listing/cloud-atlantis/sales-atlas',
          },
        },
      ],
    },
    {
      key: 'no-logo',
      label: 'A publisher with no mark',
      summary:
        'With no publisher-logo the media well draws the package glyph. The card keeps its shape, so ' +
        'a grid does not go ragged because one publisher has not uploaded a logo.',
      placement: {
        component: 'caos-listing-card',
        inputs: {
          'listing-title': 'Tax Rules',
          tagline: 'A registered, versioned calculation other metadata can call.',
          'publisher-name': 'Cloud Atlantis',
          'package-type': 'Calc Blueprint',
          'pricing-label': 'Paid',
          reviewed: true,
          href: '/listing/cloud-atlantis/tax-rules',
        },
      },
    },
  ],
};

export default docs;
